import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {TableComponent} from '../../../../../components/table/table.component';
import {FormControl, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  DateTimeSelectorComponent
} from '../../../../../components/form-inputs/date-time-selector/date-time-selector.component';
import {FormGroupComponent} from '../../../../../components/form-inputs/form-group/form-group.component';
import {Backend, PERMISSION} from '@generated-backend-types';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {FormSubmitService} from '../../../../../services/form-submit.service';
import {
  TimeTrackingEmployeeWeeklyWorkingHoursService
} from '../../../../../services/api/time-tracking/time-tracking-employee-weekly-working-hours.service';
import {DateTime} from 'luxon';
import {BsModalService, ModalModule} from 'ngx-bootstrap/modal';
import {Observable} from 'rxjs';
import {TimeTrackingConfigListBaseComponent} from '../time-tracking-config-list-base.component';
import WeekDayEnum = Backend.App.Enums.WeekDayEnum;
import TimeTrackingEmployeeWeeklyWorkingHours = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingEmployee.TimeTrackingEmployeeWeeklyWorkingHours;
import TimeTrackingEmployeeWeeklyWorkingHoursData = Backend.App.Data.Dashboard.TimeTracking.TimeTrackingEmployee.TimeTrackingEmployeeWeeklyWorkingHoursData;

type WeekDayFlags = Record<WeekDayEnum, boolean>;

interface WeeklyWorkingHoursFormValue {
  valid_from: string | null;
  hours: number | null;
  possible_work_days: WeekDayFlags;
  agreed_work_days: WeekDayFlags;
}

/** Builds a `{ monday: value, …, sunday: value }` map driven by WeekDayEnum. */
function weekDayFlags(value: boolean): WeekDayFlags {
  return Object.fromEntries(Object.values(WeekDayEnum).map(day => [day, value])) as WeekDayFlags;
}

@Component({
  selector: 'app-authenticated-time-tracking-employees-edit-weekly-working-hours',
  templateUrl: './weekly-working-hours.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslatePipe,
    TableComponent,
    FormsModule,
    ReactiveFormsModule,
    DateTimeSelectorComponent,
    FormGroupComponent,
    ModalModule,
  ],
  providers: [BsModalService],
})
export class WeeklyWorkingHoursComponent extends TimeTrackingConfigListBaseComponent {
  protected readonly translationPrefix = 'authenticated.time_tracking.employees.edit.working_hours';
  protected readonly Object = Object;
  protected readonly WeekDayEnum = WeekDayEnum;
  public form: StrictFormGroup<WeeklyWorkingHoursFormValue> = this.fb.group({
    hours: this.fb.control(null, [Validators.required]),
    valid_from: this.fb.control(null, [Validators.required]),
    possible_work_days: this.weekDayControlGroup(),
    agreed_work_days: this.weekDayControlGroup(),
  });

  public constructor(
    private readonly fb: NonNullableFormBuilder,
    formSubmitService: FormSubmitService,
    private readonly weeklyWorkingHoursService: TimeTrackingEmployeeWeeklyWorkingHoursService,
    translate: TranslateService,
    cdr: ChangeDetectorRef,
    bsModalService: BsModalService,
  ) {
    super(formSubmitService, weeklyWorkingHoursService, translate, cdr, bsModalService);
  }

  private weekDayControlGroup() {
    return this.fb.group(
      Object.fromEntries(
        Object.values(WeekDayEnum).map(day => [day, this.fb.control(false)]),
      ) as Record<WeekDayEnum, FormControl<boolean>>,
    );
  }

  protected resetForm() {
    this.form.patchValue({
      hours: null,
      valid_from: null,
      possible_work_days: weekDayFlags(false),
      agreed_work_days: weekDayFlags(false),
    });
  }

  protected submitForm(): Observable<unknown> {
    return this.formSubmitService.submit<TimeTrackingEmployeeWeeklyWorkingHours, TimeTrackingEmployeeWeeklyWorkingHoursData, TimeTrackingEmployeeWeeklyWorkingHoursService, WeeklyWorkingHoursFormValue>({
      form: this.form,
      service: this.weeklyWorkingHoursService,
      createPermission: PERMISSION.TIMETRACKING_TIMETRACKINGEMPLOYEE_TIMETRACKINGEMPLOYEEWEEKLYWORKINGHOURS_CREATE_ALL,
      createSuccessMessage: this.translate.instant('authenticated.time_tracking.employees.edit.working_hours.create.success'),
      createErrorMessage: this.translate.instant('authenticated.time_tracking.employees.edit.working_hours.create.error'),
      dataTransformer: data => {
        const possible_work_days: WeekDayEnum[] = [];
        const agreed_work_days: WeekDayEnum[] = [];

        for (const day of Object.values(WeekDayEnum)) {
          if (data.possible_work_days[day]) {
            possible_work_days.push(day);
          }

          if (data.agreed_work_days[day]) {
            agreed_work_days.push(day);
          }
        }

        return {
          time_tracking_employee_id: this.employee.id,

          hours: data.hours!,
          valid_from: DateTime.fromISO((data.valid_from ?? '')).toFormat('yyyy-MM-dd'),
          possible_work_days,
          agreed_work_days,
        }
      },
    });
  }
}
